import React, { ReactElement, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowRight, ChevronLeft, LayoutGrid, Pencil, Trash2 } from 'lucide-react';
import {
  ClientCompany,
  ClientCompanyProduct,
  useClientCompany,
  useDeleteClientCompany,
  useRemoveClientCompanyPerson,
  useSendClientSetPasswordEmail,
  useUpdateClientCompany,
  useOrganizationIncidents,
  useUpdateClientCompanyPerson,
} from '@data';
import { useStore } from '@data/storages';
import { Roles } from '@app/utils/types';
import { useOrganizationId } from '@app/utils/hooks';
import { useViewTransitionNav } from '@app/components/core/viewTransition';
import { useBackNavigation } from '@app/components/core/useBackNavigation';
import { capitalize, dayKey, formatTimestamp } from '@app/utils/helpers';
import { Language } from '@app/constants/enums';
import {
  CLIENT_ACCESS_HASH,
  type ClientPageTab,
  clientPath,
  clientsPath,
  clientTabPath,
  productPath,
} from '@app/constants/url';
import { cardClass, pageTitleClass, tabClass } from '@app/constants/styles';
import { TabBar } from '@app/components/core/tab-bar';
import { Button } from '@ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';
import { Input } from '@ui/components/ui/input';
import { Textarea } from '@ui/components/ui/textarea';
import { DateField } from '@app/components/core/date-range-filter';
import { Loader } from '@ui';
import { reasonOf } from '@data/utils/errors';
import { EditSheet } from '@app/components/core/edit-sheet';
import { useIsMobile } from '@ui/hooks/use-mobile';
import { toProductUrl } from '../../products/productUrl';
import AddPersonDialog from './AddPersonDialog';
import AccessList from './AccessList';
import ClientIncidents from './ClientIncidents';
import ClientCostTab from './ClientCostTab';
import ConfirmDialog from '../../ConfirmDialog';

const viewLabel = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground';

/** The signed documents, on their own card, in the order the compliance sheet lists them. */
const DOCUMENT_LINKS = ['legalFolderUrl', 'msaUrl', 'sowUrl', 'slaUrl', 'dpaUrl', 'ndaUrl'] as const;

/** The outward links on the details card. */
const DETAIL_LINKS = ['companyRegisterUrl'] as const;

/** Still one list, so every link keeps the single renderer and the single change detector. */
const LINK_FIELDS = [...DOCUMENT_LINKS, ...DETAIL_LINKS] as const;

// Matches `@MaxLength(255)` on the validator, so a long name or label is stopped where it is typed.
const MAX_TEXT_LENGTH = 255;

type LinkField = (typeof LINK_FIELDS)[number];

/** Which card is open for editing. Each saves on its own, so the other stays read-only meanwhile. */
type EditSection = 'details' | 'documents';

const storedLinks = (client?: ClientCompany): Record<LinkField, string> =>
  Object.fromEntries(LINK_FIELDS.map(field => [field, client?.[field] ?? ''])) as Record<LinkField, string>;

/** The saved record as the form holds it. Cancel restores this, so an abandoned edit cannot reach a later save. */
const draftFrom = (client?: ClientCompany) => ({
  name: client?.name ?? '',
  description: client?.description ?? '',
  otherRequirements: client?.otherRequirements ?? '',
  ...storedLinks(client),
  importantOn: client?.importantDate ? dayKey(new Date(client.importantDate)) : '',
  importantLabel: client?.importantDateLabel ?? '',
  reviewedOn: client?.lastReviewedAt ? dayKey(new Date(client.lastReviewedAt)) : '',
});

const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }): ReactElement => (
  <div className="flex flex-col gap-1 py-4 first:pt-0 last:pb-0 sm:flex-row sm:gap-6">
    <dt className={`${viewLabel} sm:w-44 sm:shrink-0 sm:pt-1.5`}>{label}</dt>
    <dd className="min-w-0 flex-1 text-sm text-foreground">{children}</dd>
  </div>
);

/** One client: what we call them, what we do for them, and who at their end can read it. */
const ClientDetail = ({ tab = 'overview' }: { tab?: ClientPageTab }): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { clientId } = useParams<{ clientId: string }>();
  const organizationId = useOrganizationId() ?? '';
  const transitionNavigate = useViewTransitionNav();
  const { label: backLabel, goBack } = useBackNavigation(clientsPath(organizationId), t('actions.backToClients'));

  const { data: client, isLoading, refetch } = useClientCompany(organizationId, clientId);

  // A product's access card links here at #access. The section only exists once the client has loaded, so
  // the scroll waits for that rather than for the route change.
  const location = useLocation();
  const { hash } = location;
  const accessSection = useRef<HTMLElement>(null);
  // The incidents table above it loads later and pushes it down, so the scroll waits for it too.
  const { isLoading: incidentsLoading } = useOrganizationIncidents(organizationId);
  useEffect(() => {
    if (hash === CLIENT_ACCESS_HASH) accessSection.current?.scrollIntoView({ block: 'start' });
  }, [hash, client, incidentsLoading, tab]);
  const { mutateAsync: updateClient } = useUpdateClientCompany(organizationId, clientId ?? '');
  const { mutateAsync: removePerson } = useRemoveClientCompanyPerson(organizationId, clientId ?? '');
  const { mutateAsync: updatePerson } = useUpdateClientCompanyPerson(organizationId, clientId ?? '');
  const { mutateAsync: sendSetPassword } = useSendClientSetPasswordEmail(organizationId, clientId ?? '');
  const { mutateAsync: removeClient } = useDeleteClientCompany(organizationId);

  // Reading is open to managers; changing anything is an admin's, which is what the routes enforce.
  const { organization } = useStore();
  const isAdmin = organization?.role === Roles.admin;

  const [editing, setEditing] = useState<EditSection | null>(null);
  const isMobile = useIsMobile();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [draft, setDraft] = useState(draftFrom());
  // Open while a save waits on the answer to "was this a review?".
  const [confirmReview, setConfirmReview] = useState(false);
  const [nameError, setNameError] = useState('');
  // A tab switch held while the reader answers for the typing it would cost.
  const [leavingTo, setLeavingTo] = useState<string | null>(null);
  // The client arrives with the query, so the draft is seeded once it does rather than at first render.
  useEffect(() => setDraft(draftFrom(client)), [client]);
  // The leave dialog says a tab change drops the typing, so it does, whether or not the router keeps this page.
  useEffect(() => {
    setEditing(null);
    setNameError('');
    setDraft(draftFrom(client));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a tab change only; the client is read, not watched
  }, [tab]);

  const name = draft.name.trim();
  const description = draft.description.trim();
  const nameChanged = !!name && name !== client?.name;
  const descriptionChanged = description !== (client?.description ?? '');
  const otherRequirements = draft.otherRequirements.trim();
  const otherRequirementsChanged = otherRequirements !== (client?.otherRequirements ?? '');
  // One diff for every link, so none of them can end up wired to another: a scheme added, null once
  // emptied, and only the ones that changed sent. Asked per card, so a save carries that card alone.
  const changedLinks = (fields: readonly LinkField[]): Partial<Record<LinkField, string | null>> =>
    Object.fromEntries(
      fields
        .map(field => [field, toProductUrl(draft[field])])
        .filter(([field, next]) => ((next as string | null) ?? '') !== (client?.[field as LinkField] ?? '')),
    ) as Partial<Record<LinkField, string | null>>;
  const documentChanges = changedLinks(DOCUMENT_LINKS);
  const detailLinkChanges = changedLinks(DETAIL_LINKS);
  const documentsChanged = Object.keys(documentChanges).length > 0;
  const detailLinksChanged = Object.keys(detailLinkChanges).length > 0;
  const reviewedOnChanged =
    draft.reviewedOn !== (client?.lastReviewedAt ? dayKey(new Date(client.lastReviewedAt)) : '');
  // Local midnight, so it renders as the day it names rather than the one before it west of Greenwich.
  const typedReviewedAt = draft.reviewedOn ? new Date(`${draft.reviewedOn}T00:00:00`).toISOString() : null;
  const storedImportantDay = client?.importantDate ? dayKey(new Date(client.importantDate)) : '';
  const importantDateChanged = draft.importantOn !== storedImportantDay;
  // Null clears it; a day typed in is read as local midnight, as the review date is.
  const importantDate = draft.importantOn ? new Date(`${draft.importantOn}T00:00:00`).toISOString() : null;
  const importantLabel = draft.importantLabel.trim();
  const importantLabelChanged = importantLabel !== (client?.importantDateLabel ?? '');
  const somethingElseChanged =
    nameChanged ||
    descriptionChanged ||
    otherRequirementsChanged ||
    detailLinksChanged ||
    importantDateChanged ||
    importantLabelChanged;

  // Leaves the draft alone: it holds what was just saved, and the refetch reseeds it.
  const stopEditing = (): void => {
    setEditing(null);
    setNameError('');
    setConfirmReview(false);
  };

  // Both cards share the draft, so an abandoned link or review date would otherwise ride the other's save.
  const cancelEditing = (): void => {
    setDraft(draftFrom(client));
    stopEditing();
  };

  // Seeded on the way in too: opening the other card abandons this one's edits without cancelling them.
  const startEditing = (section: EditSection): void => {
    setDraft(draftFrom(client));
    setNameError('');
    setEditing(section);
  };

  // A date set by hand is already the reader's answer, so that save asks nothing.
  const reviewTyped = reviewedOnChanged && !!draft.reviewedOn;

  // The one place a refused save is explained, so both cards say the same thing the same way.
  const explainFailure = (error: unknown): void => {
    // A taken name is fixable where it was typed, so the field keeps what they wrote and says so beside it.
    if (reasonOf(error) === 'clientNameTaken') {
      setNameError(t('clientNameTaken'));
      return;
    }
    // `silentStatuses: [400]` on the patch means only a 400 reaches here unannounced; the rest have a toast.
    if ((error as { response?: { status?: number } })?.response?.status === 400) {
      toast.error(t('clientSaveRejected'));
    }
  };

  const save = async (withReview: boolean): Promise<void> => {
    // Closed on the answer, not once the save lands: its backdrop would hide the reason beside the field.
    setConfirmReview(false);
    if (!somethingElseChanged && !reviewedOnChanged && !withReview) {
      cancelEditing();
      return;
    }
    // Only a day typed in or the button asserts a review; an emptied field sends null, which clears it.
    const lastReviewedAt = withReview && !reviewTyped ? new Date().toISOString() : typedReviewedAt;
    try {
      // Only what actually changed: a rename must not resend the description, nor the other way about.
      await updateClient({
        ...(nameChanged && { name }),
        ...(descriptionChanged && { description }),
        ...detailLinkChanges,
        ...(otherRequirementsChanged && { otherRequirements }),
        ...(importantDateChanged && { importantDate }),
        ...(importantLabelChanged && { importantDateLabel: importantLabel }),
        ...((reviewedOnChanged || withReview) && { lastReviewedAt }),
      });
      stopEditing();
    } catch (error) {
      explainFailure(error);
    }
  };

  // The documents card saves on its own, and asks nothing: swapping a link is not a claim about the record.
  const saveDocuments = async (): Promise<void> => {
    if (!documentsChanged) {
      cancelEditing();
      return;
    }
    try {
      await updateClient(documentChanges);
      stopEditing();
    } catch (error) {
      explainFailure(error);
    }
  };

  // A save with nothing to save records nothing: a review on its own is asserted by setting the date.
  const saveRequested = (): void => {
    if (!somethingElseChanged && !reviewedOnChanged) {
      cancelEditing();
      return;
    }
    if (reviewTyped) {
      save(false);
      return;
    }
    setConfirmReview(true);
  };

  // A tab replaces the current entry, so back leaves the client rather than walking through its tabs.
  const leaveFor = (to: string): void => transitionNavigate(to, 'fade', { replace: true, state: location.state });
  const switchTab = (e: React.MouseEvent, to: string, active: boolean): void => {
    if (active || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    // Each tab is its own route, so the other one mounts a fresh page and the draft goes with this one.
    const typed = !!editing && (somethingElseChanged || reviewedOnChanged || documentsChanged);
    if (typed) setLeavingTo(to);
    else leaveFor(to);
  };

  const back = (
    <button
      type="button"
      onClick={goBack}
      className="mb-3.5 flex w-fit items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
    >
      <ChevronLeft className="size-4" />
      {backLabel}
    </button>
  );

  if (isLoading) {
    return (
      <div className="w-full">
        {back}
        <Loader />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="w-full">
        {back}
        <p className="p-4 text-sm text-muted-foreground">{t('noData')}</p>
      </div>
    );
  }

  const dash = <span className="text-muted-foreground">—</span>;

  /** One outward link: an input while its own card is editing, the address as a link when not, a dash
      when unset. The field decides which card that is, so every call site stays a single line. */
  const renderLinkRow = (section: EditSection | null, field: LinkField, label: string, hint?: string): ReactElement => (
    <DetailRow key={field} label={label}>
      {/* eslint-disable-next-line no-nested-ternary -- read state has two forms: a link, or none */}
      {section === ((DOCUMENT_LINKS as readonly string[]).includes(field) ? 'documents' : 'details') ? (
        <div className="max-w-[22rem]">
          <Input
            aria-label={label}
            value={draft[field]}
            onChange={e => setDraft(current => ({ ...current, [field]: e.target.value }))}
            autoComplete="off"
            className="bg-popover"
          />
          {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
        </div>
      ) : client[field] ? (
        <a
          href={client[field]}
          target="_blank"
          rel="noreferrer"
          className="text-link underline-offset-auto hover:underline"
        >
          {client[field]}
        </a>
      ) : (
        dash
      )}
    </DetailRow>
  );

  // A phone edits in a sheet (below), leaving both cards as they read.
  const inlineSection = isMobile ? null : editing;

  const detailsRows = (section: EditSection | null): ReactElement => (
    <dl className="divide-y divide-border">
      <DetailRow label={t('titles.name')}>
        {section === 'details' ? (
          <>
            <Input
              autoFocus
              aria-label={t('titles.name')}
              aria-invalid={!!nameError}
              maxLength={MAX_TEXT_LENGTH}
              value={draft.name}
              onChange={e => {
                setDraft(current => ({ ...current, name: e.target.value }));
                setNameError('');
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  // Taken here, or the phone sheet's form submits the same save a second time.
                  e.preventDefault();
                  saveRequested();
                }
                if (e.key === 'Escape') cancelEditing();
              }}
              className="max-w-xs bg-popover"
            />
            {nameError && (
              <p role="alert" className="mt-1.5 text-xs font-medium text-destructive">
                {nameError}
              </p>
            )}
          </>
        ) : (
          client.name
        )}
      </DetailRow>
      <DetailRow label={t('titles.description')}>
        {section === 'details' ? (
          <Textarea
            aria-label={t('titles.description')}
            rows={3}
            value={draft.description}
            onChange={e => setDraft(current => ({ ...current, description: e.target.value }))}
            className="bg-popover"
          />
        ) : (
          client.description || dash
        )}
      </DetailRow>
      <DetailRow label={t('status')}>{client.isActive ? t('activeClient') : t('inactiveClient')}</DetailRow>
      {renderLinkRow(section, 'companyRegisterUrl', t('titles.companyRegister'), t('companyRegisterHint'))}
      <DetailRow label={t('titles.otherRequirements')}>
        {section === 'details' ? (
          <Textarea
            aria-label={t('titles.otherRequirements')}
            rows={3}
            value={draft.otherRequirements}
            onChange={e => setDraft(current => ({ ...current, otherRequirements: e.target.value }))}
            className="bg-popover"
          />
        ) : (
          client.otherRequirements || dash
        )}
      </DetailRow>
      <DetailRow label={t('titles.importantDate')}>
        {/* eslint-disable-next-line no-nested-ternary -- read state has two forms: a date, or none */}
        {section === 'details' ? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <DateField
                aria-label={t('titles.importantDate')}
                value={draft.importantOn}
                onChange={e => setDraft(current => ({ ...current, importantOn: e.target.value }))}
              />
              <Input
                aria-label={t('importantDateLabelPlaceholder')}
                placeholder={t('importantDateLabelPlaceholder')}
                maxLength={MAX_TEXT_LENGTH}
                value={draft.importantLabel}
                onChange={e => setDraft(current => ({ ...current, importantLabel: e.target.value }))}
                className="max-w-[14rem] bg-popover"
              />
            </div>
            <p className="text-xs text-muted-foreground">{t('importantDateHint')}</p>
          </div>
        ) : client.importantDate ? (
          <span>
            {formatTimestamp(client.importantDate, language as Language, 'dd MMM yyyy')}
            {client.importantDateLabel && (
              <span className="ml-2 text-muted-foreground">{client.importantDateLabel}</span>
            )}
          </span>
        ) : (
          dash
        )}
      </DetailRow>
      <DetailRow label={t('titles.latestReview')}>
        {/* eslint-disable-next-line no-nested-ternary -- read state has two forms: a review, or none */}
        {section === 'details' ? (
          <DateField
            aria-label={t('titles.latestReview')}
            value={draft.reviewedOn}
            max={dayKey(new Date())}
            onChange={e => setDraft(current => ({ ...current, reviewedOn: e.target.value }))}
          />
        ) : client.lastReviewedAt ? (
          t('reviewedBy', {
            date: formatTimestamp(client.lastReviewedAt, language as Language, 'dd MMM yyyy'),
            name: client.lastReviewedByName ?? '',
          })
        ) : (
          <span className="text-muted-foreground">{t('neverReviewed')}</span>
        )}
      </DetailRow>
      <DetailRow label={t('titles.created')}>
        {formatTimestamp(client.createdAt, language as Language, 'dd MMM yyyy')}
      </DetailRow>
    </dl>
  );

  const documentRows = (section: EditSection | null): ReactElement => (
    <dl className="divide-y divide-border">
      {renderLinkRow(section, 'legalFolderUrl', t('titles.legalFolder'), t('legalFolderHint'))}
      {renderLinkRow(section, 'msaUrl', t('titles.msa'))}
      {renderLinkRow(section, 'sowUrl', t('titles.sow'))}
      {renderLinkRow(section, 'slaUrl', t('titles.sla'))}
      {renderLinkRow(section, 'dpaUrl', t('titles.dpa'))}
      {renderLinkRow(section, 'ndaUrl', t('titles.nda'))}
    </dl>
  );

  const renderProductRow = (product: ClientCompanyProduct): ReactElement => {
    const open = (): void =>
      transitionNavigate(productPath(organizationId, product.id), 'forward', {
        state: {
          backPath: clientPath(organizationId, client.id),
          backLabel: t('actions.backToName', { name: client.name }),
        },
      });
    return (
      <tr key={product.id} onClick={open} className="group cursor-pointer transition-colors hover:bg-accent">
        <td className="px-6 py-3.5">
          <span className="flex min-w-0 items-center gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <LayoutGrid className="size-4" />
            </span>
            <span className="truncate font-medium text-foreground">{product.name}</span>
          </span>
        </td>
        <td className="px-6 py-3.5">{product.status ? capitalize(product.status) : dash}</td>
        <td className="px-6 py-3.5 text-right">
          <span className="inline-flex items-center justify-end gap-1.5 text-sm font-medium text-muted-foreground transition-colors group-hover:text-foreground">
            {t('viewProduct')}
            <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        </td>
      </tr>
    );
  };

  return (
    <div className="w-full">
      {back}

      <div className="mb-6 flex items-center justify-between gap-4">
        <h1 className={cx('m-0', pageTitleClass)}>{client.name}</h1>
        {/* Offered only when the API would allow it: a client whose products carry reported time is one
            we have actually worked for, and the hours would be left pointing at nobody. */}
        {isAdmin && client.canBeRemoved && (
          // Behind a confirm, as taking somebody's access away is: it cannot be undone from here, and it
          // is one click from the page you land on after creating a client.
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 bg-card text-destructive"
            onClick={() => setConfirmRemove(true)}
          >
            <Trash2 className="size-4" />
            {t('removeClient')}
          </Button>
        )}
      </div>

      <Dialog open={confirmRemove} onOpenChange={next => !next && setConfirmRemove(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('confirmRemoveTitle', { name: client.name })}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground">{t('removeClientDescription')}</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmRemove(false)}>
              {t('actions.cancel')}
            </Button>
            <Button
              type="button"
              onClick={async () => {
                try {
                  await removeClient(client.id);
                } catch {
                  // `canBeRemoved` was true when the page was read, so this is somebody reporting time on
                  // one of their products in between. The global error toast has already said so; keeping
                  // the dialog open means the refusal is not announced over a page that has navigated away.
                  return;
                }
                toast.success(t('clientRemoved'));
                setConfirmRemove(false);
                goBack();
              }}
            >
              {t('actions.remove')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asked on the way out rather than set by the app: the date is only worth anything if it says a
          person checked the record, and only they know whether they did. */}
      <Dialog open={confirmReview} onOpenChange={next => !next && setConfirmReview(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('markReviewedTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground">{t('markReviewedDescription')}</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => save(false)}>
              {t('actions.notNow')}
            </Button>
            <Button type="button" onClick={() => save(true)}>
              {t('markAsReviewed')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!leavingTo}
        onOpenChange={next => !next && setLeavingTo(null)}
        title={t('budget.unsaved.title')}
        description={t('clientUnsavedHint')}
        confirmLabel={t('budget.unsaved.leave')}
        onConfirm={() => {
          if (leavingTo) leaveFor(leavingTo);
          setLeavingTo(null);
        }}
      />

      <TabBar wrapperClassName="mb-4 print:hidden" activeKey={tab}>
        {(['overview', 'cost'] as const).map(value => {
          const active = value === tab;
          const to = clientTabPath(organizationId, client.id, value);
          return (
            <Link
              key={value}
              to={to}
              state={location.state}
              onClick={e => switchTab(e, to, active)}
              aria-current={active ? 'page' : undefined}
              className={`${tabClass(active)} inline-flex items-center`}
            >
              {t(`tabs.${value}`)}
            </Link>
          );
        })}
      </TabBar>

      {tab === 'cost' && (
        <ClientCostTab organizationId={organizationId} clientId={client.id} products={client.products} />
      )}

      {tab === 'overview' && (
        <>
          <section className={`${cardClass} mb-6`}>
            <div className="mb-5 flex items-center justify-between gap-4">
              <h2 className="text-lg font-bold text-foreground">{t('titles.details')}</h2>
              {inlineSection === 'details' ? (
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={cancelEditing}>
                    {t('actions.cancel')}
                  </Button>
                  <Button
                    size="sm"
                    disabled={!name || (!somethingElseChanged && !reviewedOnChanged)}
                    onClick={saveRequested}
                  >
                    {t('actions.save')}
                  </Button>
                </div>
              ) : (
                isAdmin && (
                  <Button variant="outline" size="sm" onClick={() => startEditing('details')}>
                    <Pencil className="size-4" />
                    {t('edit')}
                  </Button>
                )
              )}
            </div>
            {detailsRows(inlineSection)}
            <p className="mt-4 text-xs text-muted-foreground">{t('activeClientHint')}</p>
          </section>

          {/* Their own card rather than seven more rows on Details: they are one thing — what we have signed
          with this client — and the folder is where the rest of it lives. */}
          <section className={`${cardClass} mb-6`}>
            <div className="mb-5 flex items-center justify-between gap-4">
              <h2 className="text-lg font-bold text-foreground">{t('titles.documents')}</h2>
              {inlineSection === 'documents' ? (
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={cancelEditing}>
                    {t('actions.cancel')}
                  </Button>
                  <Button size="sm" disabled={!documentsChanged} onClick={saveDocuments}>
                    {t('actions.save')}
                  </Button>
                </div>
              ) : (
                isAdmin && (
                  <Button variant="outline" size="sm" onClick={() => startEditing('documents')}>
                    <Pencil className="size-4" />
                    {t('edit')}
                  </Button>
                )
              )}
            </div>
            {documentRows(inlineSection)}
          </section>

          <EditSheet
            open={isMobile && editing === 'details'}
            title={t('titles.details')}
            onCancel={cancelEditing}
            onSubmit={event => {
              event.preventDefault();
              saveRequested();
            }}
            canSave={!!name && (somethingElseChanged || reviewedOnChanged)}
          >
            {detailsRows('details')}
          </EditSheet>
          <EditSheet
            open={isMobile && editing === 'documents'}
            title={t('titles.documents')}
            onCancel={cancelEditing}
            onSubmit={event => {
              event.preventDefault();
              saveDocuments();
            }}
            canSave={documentsChanged}
          >
            {documentRows('documents')}
          </EditSheet>

          <section className="mb-6">
            <h2 className="mb-3 text-lg font-bold text-foreground">{t('titles.products')}</h2>
            {client.products.length ? (
              <div className={cx(cardClass, 'overflow-hidden p-0')}>
                {/* Relative, so the column's sr-only label is clipped here rather than widening the page. */}
                <div className="relative overflow-x-auto">
                  <table className="w-full min-w-[32rem] text-sm">
                    <thead>
                      <tr className={`border-b border-border ${viewLabel}`}>
                        <th className="px-6 py-3 text-left font-semibold">{t('titles.product')}</th>
                        <th className="w-32 px-6 py-3 text-left font-semibold">{t('status')}</th>
                        <th className="w-32 px-6 py-3">
                          <span className="sr-only">{t('viewProduct')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">{client.products.map(renderProductRow)}</tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('noProductsYet')}</p>
            )}
          </section>

          <ClientIncidents
            organizationId={organizationId}
            clientId={client.id}
            clientName={client.name}
            products={client.products}
          />

          <section ref={accessSection} id="access" className={cardClass}>
            <div className="mb-3 flex items-center justify-between gap-4">
              <h2 className="text-lg font-bold text-foreground">{t('access.peopleWithAccess')}</h2>
              {isAdmin && (
                <AddPersonDialog
                  organizationId={organizationId}
                  clientId={client.id}
                  products={client.products}
                  onAdded={refetch}
                />
              )}
            </div>
            <AccessList
              people={client.people ?? []}
              products={client.products}
              canEdit={isAdmin}
              onChange={values => updatePerson(values)}
              onRemove={membershipId => removePerson(membershipId)}
              onSendSetPassword={membershipId => sendSetPassword(membershipId)}
            />
          </section>
        </>
      )}
    </div>
  );
};

export default ClientDetail;
